/**
 * The picker: which model in a repo, when the repo holds more than one.
 *
 * **One consumer now, and that is a change worth noting** (2026-08-12). This was
 * a full-screen state *and* a dialog, kept as a pair on the rule that a modal
 * wants content behind it — true for switching a loaded model's part, false for
 * a bare deep link, which had nothing behind it. The owner's answer was that a
 * bare deep link does have something behind it: the welcome screen, which makes
 * dismissal a real answer rather than a dead end. So the full-screen state is
 * gone and `ui/compare-dialog.ts` is the only chrome.
 *
 * The `headline` flag survives that: a dialog names itself through its own
 * title, so it asks for the rows without one.
 *
 * Shown instead of a refusal. Nine of the trending-100 were turned away with
 * "non-standard layout" when in fact they were ordinary layouts holding several
 * ordinary models — a text encoder beside a transformer beside a VAE, or one
 * model at eight precisions. Those are different subjects with different layer
 * stacks, so there is no primary to fall back on and picking one would open a
 * VAE for someone who came for a transformer.
 *
 * Extracted from `main.ts` (2026-08-12) so **A's full-screen picker and B's
 * dialog are one implementation**. B could not name a quantization at all
 * before that — `loadCompare` read the repo with no part, a multi-part repo
 * threw, and comparing two GGUF quantizations was impossible. Adding a second
 * picker for B would have been two renderers for one question, drifting apart
 * on the next naming change.
 *
 * Pure: components in, markup out. The caller wires `[data-part]`, whose value
 * is the component's **label** — the same string `?part=` carries, so a click
 * and a shared link resolve through `chooseComponent` identically.
 */

import { ggufRepoSummary } from "../gguf/repo-summary.js";
import type { RepoComponent } from "../hf/components.js";
import { escapeXml as escapeHtml } from "../render/card-map.js";
import { formatBytes } from "./format.js";

export interface ChoiceChrome {
  /**
   * Whether to print the headline. The **rows are the shared thing**; the
   * heading belongs to whichever chrome is around them.
   *
   * A dialog names itself — `aria-labelledby` points at its own title — so the
   * renderer printing one too said "One model, 25 quantizations" twice, once
   * inside the other. The page has no other title and keeps it.
   */
  readonly headline?: boolean;
  /**
   * The repo summary the share card prints, when membership could be checked.
   *
   * **Passed in, never computed here.** `ggufRepoSummary` must never be handed
   * an unfiltered list, and the surest way to guarantee that is for this module
   * — which only ever sees `RepoComponent`s and could not filter them — to be
   * unable to call it. The caller holds the sizes and the parameter total, so
   * the caller is the only place the check can happen.
   */
  readonly summary?: string | undefined;
  /**
   * Files excluded as not-this-model, with what they measured.
   *
   * **Disclosed, never silent.** Selection is an identity claim, and this
   * project's rule is that an identity claim gets established, verified and
   * *disclosed* — `explainGgufSelection` was written for exactly this and had
   * no consumer until now. A visitor who came for `mtp-…-Q4_0` and cannot find
   * it in the list must be able to see that we dropped it and why, rather than
   * conclude the repo does not have it.
   */
  readonly excluded?: readonly {
    readonly label: string;
    readonly bitsPerParam: number;
  }[];
}

export function renderComponentChoice(
  components: readonly RepoComponent[],
  kind: "models" | "quantizations",
  chrome: ChoiceChrome = {},
): string {
  /*
   * The two questions are not the same question, and answering the second with
   * the first's words says something false.
   *
   * A safetensors pipeline holds *different models* — a text encoder beside a
   * transformer beside a VAE — with unrelated layer stacks. A GGUF repo holds
   * *one model many times*, at precisions from 2 to 16 bits. Calling those "22
   * models" told a reader there were twenty-two subjects here when there is one
   * (seen on `unsloth/Qwen3.6-27B-MTP-GGUF`, 2026-08-09).
   */
  const quant = kind === "quantizations";
  const heading = quant
    ? `One model, ${components.length} quantizations`
    : `This repo holds ${components.length} models`;
  const subhead = quant
    ? "Pick the precision to look inside"
    : "Pick the one to look inside";

  /*
   * Rows rather than chips, sorted by width rather than by name.
   *
   * A chip grid asks a reader to choose between up to fifty strings whose only
   * difference is llama.cpp's naming, and alphabetical order scatters the very
   * axis they are choosing on: `UD-IQ4_XS` landed between the Q2s and the Q3s,
   * and `UD-Q3_K_M` sat apart from `UD-Q3_K_S`. The decision is "how small, and
   * at what cost in precision", so the list is a ramp along exactly that.
   *
   * Anything without a cost keeps its place at the end: a safetensors pipeline
   * is not a ramp, and neither is a GGUF repo whose sizes request failed.
   */
  const rows = [...components].sort((a, b) => {
    const aw = a.bitsPerWeight ?? a.bytes;
    const bw = b.bitsPerWeight ?? b.bytes;
    if (aw === undefined && bw === undefined) return 0;
    if (aw === undefined) return 1;
    if (bw === undefined) return -1;
    return aw - bw;
  });
  const detailed = rows.some((c) => c.bytes !== undefined);

  /*
   * Two lines, and the second is a sentence of its own.
   *
   * "No weights have been downloaded" is the site's central claim, and it was
   * riding at the end of a clause about file lists where it read as a footnote
   * to a different point (owner, 2026-08-09). On its own line it is the thing
   * it is.
   *
   * "No weights" rather than "nothing": this screen *has* just made a metadata
   * request, and after that request the older wording was no longer strictly
   * true. The precision matters more here than anywhere, because it is the
   * sentence a reader is being asked to take on trust.
   */
  const hintLines = quant
    ? detailed
      ? [
          `Every one of these is the same model at a different precision. Sizes and widths come
           from the repo's file list.`,
          "No weights have been downloaded.",
        ]
      : [
          `Every one of these is the same model at a different precision, from about 2 bits per
           weight to 16. They are separate files with their own tensor indexes, so they are read
           one at a time — and the map's numbers describe whichever you pick.`,
        ]
    : [
        `Pipelines keep each component in its own directory — a text encoder, a transformer and a
         decoder are separate models with unrelated layer stacks, so they are read one at a time
         rather than merged into one grid.`,
      ];

  const chip = (c: RepoComponent): string =>
    // `btn example-chip`, matching the landing row exactly. `.chip` is a
    // different component — the aria-pressed filter chip — and wearing both
    // would inherit a combination neither was designed for.
    `<button type="button" class="btn example-chip" data-part="${escapeHtml(
      c.label,
    )}"><span class="example-name">${escapeHtml(c.label)}</span></button>`;

  const row = (c: RepoComponent): string => {
    const size = c.bytes === undefined ? "" : formatBytes(c.bytes);
    /*
     * Always `≈`. This is file size over parameters, so every row carries the
     * same ~10 MiB tokenizer header and divides by the same parameter count —
     * the absolute error is identical down the column. Marking only some rows
     * implied one was measured more precisely than another, which was never
     * true, and the threshold that did the marking missed by enough to print
     * 5.21 where the anatomy says 5.19. See `gguf/variant-sizes.ts`.
     */
    const width = c.bitsPerWeight === undefined ? "" : `≈${c.bitsPerWeight.toFixed(2)}`;
    const note = c.fileCount && c.fileCount > 1 ? `${c.fileCount} files` : "";
    return `<button type="button" class="btn variant-row" data-part="${escapeHtml(c.label)}">
      <span class="variant-name mono">${escapeHtml(c.label)}</span>
      <span class="variant-size mono">${escapeHtml(size)}</span>
      <span class="variant-bpw mono">${escapeHtml(width)}</span>
      <span class="variant-note">${escapeHtml(note)}</span>
    </button>`;
  };

  return `<div class="empty-state">
    <div class="empty-title">
      ${
        chrome.headline === false
          ? ""
          : `<h2 class="empty-headline">${escapeHtml(heading)}</h2>`
      }
      <p class="empty-subhead">${escapeHtml(subhead)}</p>
      ${
        chrome.summary === undefined
          ? ""
          : `<p class="empty-summary mono">${escapeHtml(chrome.summary)}</p>`
      }
    </div>
    ${
      detailed
        ? `<div class="variant-rows">
             <div class="variant-head" aria-hidden="true">
               <span></span><span>size</span><span>bits/weight</span><span></span>
             </div>
             ${rows.map(row).join("")}
           </div>`
        : `<div class="example-repos">${rows.map(chip).join("")}</div>`
    }
    ${
      !chrome.excluded || chrome.excluded.length === 0
        ? ""
        : `<p class="prose prose--muted example-hint">${escapeHtml(
            `Not listed: ${chrome.excluded
              .map(
                (e) => `${e.label} (${e.bitsPerParam.toFixed(2)} bits per parameter)`,
              )
              .join(", ")} — too small to be this model at any quantization, so ${
              chrome.excluded.length === 1 ? "it is" : "they are"
            } a projector, adapter or draft head rather than a copy of it.`,
          )}</p>`
    }
    ${hintLines
      .map(
        (line) =>
          `<p class="prose prose--muted example-hint">${escapeHtml(line.replace(/\s+/g, " ").trim())}</p>`,
      )
      .join("")}
  </div>`;
}
